import React, { FC, ReactNode } from 'react'
import { Box } from '@mui/material'
import { BaseScale } from './BaseScale'

/**
 * Para el `<Form>` de Formik (o cualquier contenedor propio) que quede entre
 * el DialogContent y un ModalScrollableContent con `fill`. La cadena flex se
 * corta en el primer eslabon que no la propague: sin esto el bloque con
 * scroll no tiene contra que crecer y colapsa al alto de su contenido.
 */
export const fillFormStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0
}

interface ModalScrollableContentProps {
  /** Contenido largo del modal (scrollea internamente si excede maxHeight) */
  children: ReactNode
  /** Botones de acción (Cancelar/Guardar, etc.) — siempre visibles, nunca scrollean */
  actions: ReactNode
  /**
   * Alto máximo total del bloque (contenido + acciones). Ajustar según cuánto
   * contenido fijo exista ANTES de este componente dentro del mismo modal
   * (ej. un banner o header que quede fuera del wrapper).
   * Default: 'calc(100vh - 260px)' (pensado para cuando este componente
   * envuelve prácticamente todo el contenido del modal, sin headers extra afuera).
   */
  maxHeight?: string | number
  /**
   * Llena el alto que le deja su contenedor en vez de acotarse con
   * `maxHeight`. Requiere que el modal corra con `fillHeight` y que los
   * contenedores intermedios (p. ej. el `<Form>` de Formik) sean columnas
   * flex con `minHeight: 0` — sin eso el bloque no tiene un alto contra el
   * que crecer y colapsa al de su contenido.
   */
  fill?: boolean
}

/**
 * Envuelve el contenido largo de un modal junto con sus botones de acción en
 * un único contenedor flex-column acotado (overflow: hidden). Adentro, el
 * contenido scrollea de forma independiente (flex: 1; overflow: auto;
 * minHeight: 0) mientras que las acciones quedan como un hijo flex fijo
 * después de él, fuera del área con scroll — por lo tanto siempre visibles,
 * sin depender de position: sticky (que no funciona cuando el bloque de
 * acciones es el último elemento de su contenedor con scroll).
 *
 * children y actions viajan juntos en la misma llamada a propósito: así no
 * es posible "olvidar" envolver ambos dentro del mismo contenedor acotado,
 * que es lo que hace que el patrón funcione.
 */
export const ModalScrollableContent: FC<ModalScrollableContentProps> = ({
  children,
  actions,
  maxHeight = 'calc(100vh - 260px)',
  fill = false
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      ...(fill ? { flex: '1 1 auto', minHeight: 0 } : { maxHeight })
    }}
  >
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
      {children}
    </Box>
    <Box
      sx={{
        flex: '0 0 auto',
        display: 'flex',
        gap: 2,
        justifyContent: 'flex-end',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        pt: 2,
        mt: 2
      }}
    >
      {/* Los botones quedan a la escala del tema de la app aunque el modal
          corra en modo compacto: son los mismos Cancelar/Guardar que en
          cualquier otro mantenedor. No-op si el modal no es compacto. */}
      <BaseScale>{actions}</BaseScale>
    </Box>
  </Box>
)
